import { useState, useEffect } from 'react'
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  Grid,
  CircularProgress,
  Alert,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  IconButton,
  FormControlLabel,
  Switch,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
  ToggleButtonGroup,
  ToggleButton,
  Tooltip,
} from '@mui/material'
import {
  Save,
  AddPhotoAlternate,
  Add,
  Delete,
  Facebook,
  Instagram,
  Twitter,
  LinkedIn,
  YouTube,
  GitHub,
  Language as WebIcon,
  ExpandMore,
  Close,
  Lock,
  People,
  Public,
  Groups,
} from '@mui/icons-material'
import {
  FaTiktok,
  FaDiscord,
  FaPinterest,
  FaWhatsapp,
  FaSnapchat,
  FaTelegram,
  FaWeixin,
} from 'react-icons/fa'
import '../../../adventures/components/editor/styles/TagsPanel.css'
import { profileApi } from '@api/endpoints/profile'
import { uploadsApi } from '@api/endpoints/uploads'
import type { UserProfileDto, UpdateUserProfileDto, ProfileVisibility, AgeVisibility, SocialMediaLinkDto } from '@types/profile'
import { useCachedTags } from '@hooks/useCachedTags'
import { TagType } from '@features/adventures/types/adventure-editor.types'
import { ErrorDialog } from '@components/ErrorDialog'
import { getSaveFriendlyError, type FriendlyError } from '@utils/errorHandling'
import { ImageUploadZone } from '@components/common/ImageUploadZone'
import { ProfileImageSelectorDialog } from './ProfileImageSelectorDialog'
import type { UserFileDto } from '@api/types/files'

// Social media platform icons
const platformIcons: Record<string, React.ReactElement> = {
  Facebook: <Facebook />,
  Instagram: <Instagram />,
  Twitter: <Twitter />,
  LinkedIn: <LinkedIn />,
  YouTube: <YouTube />,
  TikTok: <FaTiktok size={24} />,
  Discord: <FaDiscord size={24} />,
  Pinterest: <FaPinterest size={24} />,
  WhatsApp: <FaWhatsapp size={24} />,
  Snapchat: <FaSnapchat size={24} />,
  Telegram: <FaTelegram size={24} />,
  WeChat: <FaWeixin size={24} />,
  GitHub: <GitHub />,
  Other: <WebIcon />,
}

const platformOptions = ['Facebook', 'Instagram', 'Twitter', 'LinkedIn', 'YouTube', 'TikTok', 'Discord', 'Pinterest', 'WhatsApp', 'Snapchat', 'Telegram', 'WeChat', 'GitHub', 'Other']

// Helper function to calculate age in months from month/year
function calculateAgeInMonths(month: number, year: number): number {
  const birthDate = new Date(year, month - 1, 1)
  const now = new Date()
  const months = (now.getFullYear() - birthDate.getFullYear()) * 12 + (now.getMonth() - birthDate.getMonth())
  return Math.max(0, months)
}

// Helper function to convert ageInMonths back to approximate month/year
function ageInMonthsToMonthYear(ageInMonths: number | null): { month: number; year: number } {
  if (!ageInMonths) {
    const currentYear = new Date().getFullYear()
    return { month: 1, year: currentYear - 18 } // Default to 18 years ago
  }

  const now = new Date()
  const birthDate = new Date(now.getFullYear(), now.getMonth() - ageInMonths, 1)

  return {
    month: birthDate.getMonth() + 1,
    year: birthDate.getFullYear(),
  }
}

// Social Media URL Helpers
// IMPORTANT: We store ONLY the username for known platforms (Facebook, Instagram, etc.)
// and generate the full URL on-the-fly when needed. This reduces storage, improves security,
// and makes it easier to update URL patterns if platforms change their structure.
// Exception: "Other" platform stores the full URL since we can't predict the format.
//
// SECURITY MEASURES:
// 1. Input validation blocks: path traversal (../), script injection, template literals, etc.
// 2. Username format strictly limited to: alphanumeric, dots, underscores, hyphens (max 100 chars)
// 3. "Other" platform URLs validated as proper http/https URLs only
// 4. URL generation sanitizes output to prevent injection attacks
// 5. Double validation on both input and output for defense in depth
const PLATFORM_PATTERNS: Record<string, { baseUrl: string; pattern: RegExp[] }> = {
  Facebook: {
    baseUrl: 'https://facebook.com/',
    pattern: [/(?:https?:\/\/)?(?:www\.)?facebook\.com\/([a-zA-Z0-9._-]+)/i],
  },
  Instagram: {
    baseUrl: 'https://instagram.com/',
    pattern: [/(?:https?:\/\/)?(?:www\.)?instagram\.com\/([a-zA-Z0-9._]+)/i],
  },
  Twitter: {
    baseUrl: 'https://twitter.com/',
    pattern: [
      /(?:https?:\/\/)?(?:www\.)?twitter\.com\/([a-zA-Z0-9_]+)/i,
      /(?:https?:\/\/)?(?:www\.)?x\.com\/([a-zA-Z0-9_]+)/i,
    ],
  },
  LinkedIn: {
    baseUrl: 'https://linkedin.com/in/',
    pattern: [/(?:https?:\/\/)?(?:www\.)?linkedin\.com\/in\/([a-zA-Z0-9_-]+)/i],
  },
  YouTube: {
    baseUrl: 'https://youtube.com/@',
    pattern: [
      /(?:https?:\/\/)?(?:www\.)?youtube\.com\/@([a-zA-Z0-9_-]+)/i,
      /(?:https?:\/\/)?(?:www\.)?youtube\.com\/c\/([a-zA-Z0-9_-]+)/i,
      /(?:https?:\/\/)?(?:www\.)?youtube\.com\/user\/([a-zA-Z0-9_-]+)/i,
      /(?:https?:\/\/)?(?:www\.)?youtube\.com\/channel\/([a-zA-Z0-9_-]+)/i,
    ],
  },
  GitHub: {
    baseUrl: 'https://github.com/',
    pattern: [/(?:https?:\/\/)?(?:www\.)?github\.com\/([a-zA-Z0-9_-]+)/i],
  },
  TikTok: {
    baseUrl: 'https://tiktok.com/@',
    pattern: [
      /(?:https?:\/\/)?(?:www\.)?tiktok\.com\/@([a-zA-Z0-9._-]+)/i,
      /(?:https?:\/\/)?(?:vm\.)?tiktok\.com\/([a-zA-Z0-9]+)/i,
    ],
  },
  Discord: {
    baseUrl: 'https://discord.com/users/',
    pattern: [
      /(?:https?:\/\/)?(?:www\.)?discord\.com\/users\/([0-9]+)/i,
      /(?:https?:\/\/)?(?:www\.)?discord\.gg\/([a-zA-Z0-9]+)/i,
    ],
  },
  Pinterest: {
    baseUrl: 'https://pinterest.com/',
    pattern: [/(?:https?:\/\/)?(?:www\.)?pinterest\.com\/([a-zA-Z0-9_-]+)/i],
  },
  WhatsApp: {
    baseUrl: 'https://wa.me/',
    pattern: [
      /(?:https?:\/\/)?wa\.me\/([0-9]+)/i,
      /(?:https?:\/\/)?(?:www\.)?whatsapp\.com\/([a-zA-Z0-9]+)/i,
    ],
  },
  Snapchat: {
    baseUrl: 'https://snapchat.com/add/',
    pattern: [
      /(?:https?:\/\/)?(?:www\.)?snapchat\.com\/add\/([a-zA-Z0-9._-]+)/i,
      /(?:https?:\/\/)?(?:www\.)?snapchat\.com\/t\/([a-zA-Z0-9]+)/i,
      /(?:https?:\/\/)?story\.snapchat\.com\/([a-zA-Z0-9._-]+)/i,
    ],
  },
  Telegram: {
    baseUrl: 'https://t.me/',
    pattern: [
      /(?:https?:\/\/)?t\.me\/([a-zA-Z0-9_]+)/i,
      /(?:https?:\/\/)?telegram\.me\/([a-zA-Z0-9_]+)/i,
    ],
  },
  WeChat: {
    baseUrl: 'https://weixin.qq.com/',
    pattern: [/(?:https?:\/\/)?weixin\.qq\.com\/([a-zA-Z0-9_-]+)/i],
  },
}

/**
 * Extract username from input (can be username or full URL)
 * Includes security validation to prevent injection attacks
 */
function extractUsernameFromInput(input: string, platform: string): string | null {
  if (!input.trim()) return null

  const trimmedInput = input.trim()

  // Security checks for malicious patterns
  const dangerousPatterns = [
    /\.\./,                           // Path traversal (..)
    /%2e%2e/i,                        // Encoded path traversal
    /[<>'"]/,                         // HTML/script injection attempts
    /javascript:/i,                   // JavaScript protocol
    /data:/i,                         // Data URI scheme
    /vbscript:/i,                     // VBScript protocol
    /on\w+=/i,                        // Event handlers (onclick=, onerror=, etc.)
    /\$\{/,                           // Template literal injection
    /`/,                              // Backticks (template literals)
    /\\/,                             // Backslashes (path manipulation)
    /file:/i,                         // File protocol
    /[\x00-\x1f\x7f-\x9f]/,          // Control characters
  ]

  for (const pattern of dangerousPatterns) {
    if (pattern.test(trimmedInput)) {
      console.warn('Rejected social media input due to security validation:', trimmedInput)
      return null
    }
  }

  // For "Other" platform, validate it's a proper URL
  if (platform === 'Other') {
    try {
      // Must start with http:// or https://
      if (!trimmedInput.match(/^https?:\/\//i)) {
        return null
      }

      // Try to parse as URL to ensure it's valid
      const url = new URL(trimmedInput)

      // Only allow http and https protocols
      if (!['http:', 'https:'].includes(url.protocol)) {
        console.warn('Rejected social media URL with invalid protocol:', url.protocol)
        return null
      }

      // Ensure there's a valid hostname
      if (!url.hostname || url.hostname.length < 3) {
        return null
      }

      return trimmedInput
    } catch (e) {
      console.warn('Rejected invalid URL:', trimmedInput)
      return null
    }
  }

  const platformConfig = PLATFORM_PATTERNS[platform]
  if (!platformConfig) return null

  // Try to match against platform URL patterns
  for (const pattern of platformConfig.pattern) {
    const match = trimmedInput.match(pattern)
    if (match && match[1]) {
      const extractedUsername = match[1]

      // Validate extracted username
      if (/^[a-zA-Z0-9._-]+$/.test(extractedUsername) && extractedUsername.length <= 100) {
        return extractedUsername
      }
    }
  }

  // If no URL pattern matched, treat as username
  // Remove @ prefix if present (common for social media handles)
  const cleanUsername = trimmedInput.startsWith('@') ? trimmedInput.slice(1) : trimmedInput

  // Strict validation: alphanumeric, dots, underscores, hyphens only
  // Length limit to prevent abuse
  if (/^[a-zA-Z0-9._-]+$/.test(cleanUsername) && cleanUsername.length > 0 && cleanUsername.length <= 100) {
    return cleanUsername
  }

  return null
}

/**
 * Generate platform URL from username
 * Includes security sanitization to prevent injection in rendered URLs
 */
function generatePlatformUrl(username: string, platform: string): string {
  if (!username) return ''

  // Sanitize username - remove any dangerous characters that might have slipped through
  const sanitizedUsername = username
    .replace(/[<>"'`\\]/g, '') // Remove HTML/script injection chars
    .replace(/\.\./g, '')       // Remove path traversal
    .trim()

  if (!sanitizedUsername) return ''

  if (platform === 'Other') {
    // For "Other", validate it starts with http:// or https://
    if (!sanitizedUsername.startsWith('http://') && !sanitizedUsername.startsWith('https://')) {
      return `https://${sanitizedUsername}`
    }

    // Additional validation for "Other" URLs
    try {
      const url = new URL(sanitizedUsername)
      // Only allow http and https
      if (['http:', 'https:'].includes(url.protocol)) {
        return sanitizedUsername
      }
    } catch {
      // Invalid URL, return empty
      return ''
    }

    return ''
  }

  const platformConfig = PLATFORM_PATTERNS[platform]
  if (!platformConfig) return ''

  // Final validation: username should only contain safe characters
  if (!/^[a-zA-Z0-9._-]+$/.test(sanitizedUsername)) {
    console.warn('Rejected username during URL generation:', sanitizedUsername)
    return ''
  }

  return `${platformConfig.baseUrl}${sanitizedUsername}`
}

/**
 * Parse social media input and return proper URL (for UI preview)
 * Note: We only store the username, not the full URL
 */
function parseSocialMediaInput(input: string, platform: string): string {
  const username = extractUsernameFromInput(input, platform)
  if (!username) return input

  return generatePlatformUrl(username, platform)
}

/**
 * Generate clickable URL from stored username
 * Use this when rendering social media links in the UI
 * @param username - The stored username (without URL prefix)
 * @param platform - The social media platform
 * @returns Full clickable URL
 */
export function generateSocialMediaUrl(username: string, platform: string): string {
  return generatePlatformUrl(username, platform)
}

// Helper function to check if display name can be changed
function canChangeDisplayName(profile: UserProfileDto | null): {
  canChange: boolean
  reason?: string
  daysUntilNextChange?: number
  changesRemaining?: number
} {
  if (!profile) return { canChange: true }

  const now = new Date()

  // Check if they've used all 2 changes this year
  if (profile.displayNameChangesThisYear >= 2) {
    // Calculate days until next year
    const nextYear = new Date(now.getFullYear() + 1, 0, 1)
    const daysUntilNextYear = Math.ceil((nextYear.getTime() - now.getTime()) / (1000 * 60 * 60 * 24))

    return {
      canChange: false,
      reason: 'You have used both of your display name changes for this year.',
      daysUntilNextChange: daysUntilNextYear,
      changesRemaining: 0,
    }
  }

  // Check 30-day cooldown
  if (profile.lastDisplayNameChange) {
    const lastChange = new Date(profile.lastDisplayNameChange)
    const daysSinceLastChange = Math.floor((now.getTime() - lastChange.getTime()) / (1000 * 60 * 60 * 24))

    if (daysSinceLastChange < 30) {
      const daysUntilNextChange = 30 - daysSinceLastChange
      return {
        canChange: false,
        reason: `You can change your display name again in ${daysUntilNextChange} day${daysUntilNextChange === 1 ? '' : 's'}.`,
        daysUntilNextChange,
        changesRemaining: 2 - profile.displayNameChangesThisYear,
      }
    }
  }

  // Can change
  return {
    canChange: true,
    changesRemaining: 2 - profile.displayNameChangesThisYear,
  }
}

export function UserInfoSection() {
  const [profile, setProfile] = useState<UserProfileDto | null>(null)

  // Internal state for tag IDs (for UI selection)
  const [selectedGenderTagId, setSelectedGenderTagId] = useState<string | null>(null)
  const [selectedInterestTagIds, setSelectedInterestTagIds] = useState<string[]>([])
  const [selectedLookingForTagIds, setSelectedLookingForTagIds] = useState<string[]>([])

  const [formData, setFormData] = useState<UpdateUserProfileDto>({
    displayName: '',
    avatar: null,
    coverPhoto: null,
    bio: '',
    hometown: '',
    profileVisibility: 0, // Public
    showAge: 0, // AgeVisibility.Private
    socialLinksVisible: true,
    ageInMonths: null,
    genderIdentityTagId: null,
    genderVisibility: 3, // Public
    socialMediaLinks: {},
    interestTagIds: [],
    interestsVisibility: 3, // Public
    lookingForTagIds: [],
    lookingForVisibility: 3, // Public
  })
  const [birthMonth, setBirthMonth] = useState(1)
  const [birthYear, setBirthYear] = useState(new Date().getFullYear() - 18)
  const [isLoading, setIsLoading] = useState(true)
  const [isSaving, setIsSaving] = useState(false)
  const [error, setError] = useState<FriendlyError | null>(null)
  const [successMessage, setSuccessMessage] = useState<string | null>(null)

  // State for controlling which accordion is expanded in each section
  const [expandedGenderCategory, setExpandedGenderCategory] = useState<string | null>(null)
  const [expandedInterestCategory, setExpandedInterestCategory] = useState<string | null>(null)
  const [expandedLookingForCategory, setExpandedLookingForCategory] = useState<string | null>(null)

  // State for tracking social media link validation errors
  const [socialLinkErrors, setSocialLinkErrors] = useState<Record<string, string>>({})

  // State for image uploads
  const [isUploadingAvatar, setIsUploadingAvatar] = useState(false)
  const [isUploadingCover, setIsUploadingCover] = useState(false)

  // State for image selectors
  const [avatarSelectorOpen, setAvatarSelectorOpen] = useState(false)
  const [coverSelectorOpen, setCoverSelectorOpen] = useState(false)

  const { getCategoriesByType, getTags, isLoading: isLoadingTags, categories } = useCachedTags()

  // Get tag categories with null checks
  const genderCategories = getCategoriesByType(TagType.GenderIdentity) || []
  const genderTags = genderCategories.flatMap(cat => cat.tags || [])
  const interestCategories = getCategoriesByType(TagType.Interests) || []
  const interestTags = interestCategories.flatMap(cat => cat.tags || [])
  const lookingForCategories = getCategoriesByType(TagType.LookingFor) || []
  const lookingForTags = lookingForCategories.flatMap(cat => cat.tags || [])

  // Debug logging
  console.log('Tags debug:', {
    isLoadingTags,
    totalCategories: categories.length,
    categoryTypes: categories.map(c => ({ name: c.name, tagType: c.tagType, tagsCount: c.tags?.length, isActive: c.isActive })),
    lookingForEnum: TagType.LookingFor,
    genderEnum: TagType.GenderIdentity,
    interestEnum: TagType.Interests,
    genderCategories: genderCategories.length,
    genderTags: genderTags.length,
    interestCategories: interestCategories.length,
    interestTags: interestTags.length,
    lookingForCategories: lookingForCategories.length,
    lookingForCategoriesDetails: lookingForCategories.map(c => ({
      name: c.name,
      displayName: c.displayName,
      isActive: c.isActive,
      tags: c.tags?.length,
      activeTags: c.tags?.filter(t => t.isActive).length
    })),
    lookingForTags: lookingForTags.length
  })

  // Load user profile
  useEffect(() => {
    loadProfile()
  }, [])

  const loadProfile = async () => {
    try {
      setIsLoading(true)
      const data = await profileApi.getMyProfile()
      setProfile(data)

      // Convert ageInMonths to month/year for display
      const { month, year } = ageInMonthsToMonthYear(data.ageInMonths)
      setBirthMonth(month)
      setBirthYear(year)

      // Set tag IDs for internal state (for UI)
      console.log('Loading profile tag data:', {
        genderIdentityTagId: data.genderIdentityTagId,
        interestTagIds: data.interestTagIds,
        lookingForTagIds: data.lookingForTagIds,
        genderIdentity: data.genderIdentity,
        interestTags: data.interestTags,
        lookingFor: data.lookingFor
      })
      setSelectedGenderTagId(data.genderIdentityTagId)
      setSelectedInterestTagIds(data.interestTagIds || [])
      setSelectedLookingForTagIds(data.lookingForTagIds || [])

      setFormData({
        displayName: data.displayName || '',
        avatar: data.avatar,
        coverPhoto: data.coverPhoto,
        bio: data.bio || '',
        hometown: data.hometown || '',
        profileVisibility: data.profileVisibility,
        showAge: data.showAge,
        socialLinksVisible: data.socialLinksVisible,
        ageInMonths: data.ageInMonths,
        genderIdentityTagId: data.genderIdentityTagId || data.genderIdentity || null,
        genderVisibility: data.genderVisibility ?? 3, // Default to Public if not set
        socialMediaLinks: data.socialMediaLinks || {},
        interestTagIds: data.interestTagIds || data.interestTags || [],
        interestsVisibility: data.interestsVisibility ?? 3, // Default to Public if not set
        lookingForTagIds: data.lookingForTagIds || data.lookingFor || [],
        lookingForVisibility: data.lookingForVisibility ?? 3, // Default to Public if not set
      })
    } catch (err) {
      console.error('Failed to load profile:', err)
      setError(getSaveFriendlyError(err))
      setIsLoading(false) // Ensure we exit loading state on error
    } finally {
      setIsLoading(false)
    }
  }

  const handleInputChange = (field: keyof UpdateUserProfileDto, value: any) => {
    setFormData({ ...formData, [field]: value })
    setSuccessMessage(null)
  }

  const handleBirthDateChange = (month: number, year: number) => {
    setBirthMonth(month)
    setBirthYear(year)
    const ageInMonths = calculateAgeInMonths(month, year)
    setFormData({ ...formData, ageInMonths })
    setSuccessMessage(null)
  }

  const handleAddSocialLink = () => {
    const newLinks = { ...formData.socialMediaLinks }

    // Get list of platforms already in use
    const usedPlatforms = Object.values(newLinks).map(link => link.platform)

    // Find the first available platform that hasn't been used
    const availablePlatform = platformOptions.find(platform => !usedPlatforms.includes(platform))

    // If no platform is available, don't add
    if (!availablePlatform) {
      return
    }

    // Use the platform name as the key
    newLinks[availablePlatform] = { platform: availablePlatform, url: '', isPublic: true, visibility: 3 } // Default to Public
    setFormData({ ...formData, socialMediaLinks: newLinks })
  }

  const handleUpdateSocialLink = (key: string, field: keyof SocialMediaLinkDto, value: any) => {
    const newLinks = { ...formData.socialMediaLinks }
    const newErrors = { ...socialLinkErrors }

    if (newLinks[key]) {
      // If updating URL, extract username only (don't store full URL)
      if (field === 'url') {
        const platform = newLinks[key].platform
        const username = extractUsernameFromInput(value, platform)

        if (!username && value.trim()) {
          // Validation failed - set error message
          newErrors[key] = 'Invalid format. Use only alphanumeric characters, dots, underscores, and hyphens.'
          newLinks[key] = { ...newLinks[key], url: value } // Keep the invalid value so user can see/fix it
        } else {
          // Validation passed - clear error
          delete newErrors[key]
          newLinks[key] = { ...newLinks[key], url: username || value }
        }
      } else {
        newLinks[key] = { ...newLinks[key], [field]: value }
      }

      // If platform changed, update the key (username stays the same)
      if (field === 'platform') {
        // Check if the new platform is already in use by another link
        const usedPlatforms = Object.entries(newLinks)
          .filter(([k, _]) => k !== key) // Exclude current link
          .map(([_, link]) => link.platform)

        if (usedPlatforms.includes(value)) {
          // Platform already in use, don't allow the change
          return
        }

        const oldLink = newLinks[key]
        const oldError = newErrors[key]
        delete newLinks[key]
        delete newErrors[key]

        // Use the platform name as the new key (no counter needed since we enforce uniqueness)
        const newKey = value
        newLinks[newKey] = { ...oldLink, platform: value }
        if (oldError) {
          newErrors[newKey] = oldError
        }
      }

      setFormData({ ...formData, socialMediaLinks: newLinks })
      setSocialLinkErrors(newErrors)
      setSuccessMessage(null)
    }
  }

  const handleRemoveSocialLink = (key: string) => {
    const newLinks = { ...formData.socialMediaLinks }
    delete newLinks[key]
    setFormData({ ...formData, socialMediaLinks: newLinks })
    setSuccessMessage(null)
  }

  const handleAvatarUpload = async (file: File) => {
    try {
      setIsUploadingAvatar(true)
      setError(null)
      setSuccessMessage(null)

      const updatedProfile = await uploadsApi.uploadAvatar(file)

      // Update form data with new avatar URL
      setFormData({ ...formData, avatar: updatedProfile.avatar })

      // Update profile state
      if (profile) {
        setProfile({ ...profile, avatar: updatedProfile.avatar })
      }

      setSuccessMessage('Avatar uploaded successfully!')
    } catch (err) {
      setError(getSaveFriendlyError(err))
    } finally {
      setIsUploadingAvatar(false)
    }
  }

  const handleCoverPhotoUpload = async (file: File) => {
    try {
      setIsUploadingCover(true)
      setError(null)
      setSuccessMessage(null)

      const updatedProfile = await uploadsApi.uploadCoverPhoto(file)

      // Update form data with new cover photo URL
      setFormData({ ...formData, coverPhoto: updatedProfile.coverPhoto })

      // Update profile state
      if (profile) {
        setProfile({ ...profile, coverPhoto: updatedProfile.coverPhoto })
      }

      setSuccessMessage('Cover photo uploaded successfully!')
    } catch (err) {
      setError(getSaveFriendlyError(err))
    } finally {
      setIsUploadingCover(false)
    }
  }

  const handleAvatarFromFiles = async (file: UserFileDto) => {
    try {
      setIsUploadingAvatar(true)
      setError(null)
      setSuccessMessage(null)
      setAvatarSelectorOpen(false)

      const updatedProfile = await profileApi.setAvatar(file.id)

      // Update form data with new avatar URL
      setFormData({ ...formData, avatar: updatedProfile.avatar })

      // Update profile state
      if (profile) {
        setProfile({ ...profile, avatar: updatedProfile.avatar })
      }

      setSuccessMessage('Avatar updated successfully!')
    } catch (err) {
      setError(getSaveFriendlyError(err))
    } finally {
      setIsUploadingAvatar(false)
    }
  }

  const handleCoverFromFiles = async (file: UserFileDto) => {
    try {
      setIsUploadingCover(true)
      setError(null)
      setSuccessMessage(null)
      setCoverSelectorOpen(false)

      const updatedProfile = await profileApi.setCoverPhoto(file.id)

      // Update form data with new cover photo URL
      setFormData({ ...formData, coverPhoto: updatedProfile.coverPhoto })

      // Update profile state
      if (profile) {
        setProfile({ ...profile, coverPhoto: updatedProfile.coverPhoto })
      }

      setSuccessMessage('Cover photo updated successfully!')
    } catch (err) {
      setError(getSaveFriendlyError(err))
    } finally {
      setIsUploadingCover(false)
    }
  }

  const handleClearAvatar = async () => {
    try {
      setIsUploadingAvatar(true)
      setError(null)
      setSuccessMessage(null)

      const updatedProfile = await profileApi.clearAvatar()

      // Update form data
      setFormData({ ...formData, avatar: null })

      // Update profile state
      if (profile) {
        setProfile({ ...profile, avatar: null })
      }

      setSuccessMessage('Avatar removed successfully!')
    } catch (err) {
      setError(getSaveFriendlyError(err))
    } finally {
      setIsUploadingAvatar(false)
    }
  }

  const handleClearCover = async () => {
    try {
      setIsUploadingCover(true)
      setError(null)
      setSuccessMessage(null)

      const updatedProfile = await profileApi.clearCoverPhoto()

      // Update form data
      setFormData({ ...formData, coverPhoto: null })

      // Update profile state
      if (profile) {
        setProfile({ ...profile, coverPhoto: null })
      }

      setSuccessMessage('Cover photo removed successfully!')
    } catch (err) {
      setError(getSaveFriendlyError(err))
    } finally {
      setIsUploadingCover(false)
    }
  }

  const handleSave = async () => {
    try {
      setIsSaving(true)
      setError(null)
      setSuccessMessage(null)

      // Check if display name has changed and if user is allowed to change it
      const displayNameChanged = profile?.displayName !== formData.displayName
      const displayNameStatus = canChangeDisplayName(profile)

      if (displayNameChanged && !displayNameStatus.canChange) {
        setError({
          title: 'Cannot Change Display Name',
          message: displayNameStatus.reason || 'You are not allowed to change your display name at this time.',
          details: [],
        })
        setIsSaving(false)
        return
      }

      // Send tag IDs to the API (not display names)
      const dataToSave: UpdateUserProfileDto = {
        ...formData,
        genderIdentityTagId: selectedGenderTagId || null,
        interestTagIds: selectedInterestTagIds.length > 0 ? selectedInterestTagIds : [],
        lookingForTagIds: selectedLookingForTagIds.length > 0 ? selectedLookingForTagIds : [],
      }

      console.log('Saving profile with tag IDs:', {
        genderIdentityTagId: dataToSave.genderIdentityTagId,
        interestTagIds: dataToSave.interestTagIds,
        lookingForTagIds: dataToSave.lookingForTagIds,
      })

      const response = await profileApi.updateMyProfile(dataToSave)

      setSuccessMessage('Profile updated successfully!')
      setProfile(response)
    } catch (err) {
      setError(getSaveFriendlyError(err))
    } finally {
      setIsSaving(false)
    }
  }

  // Tag selection helpers
  const handleToggleGenderTag = (tagId: string) => {
    // Single selection - if clicking same tag, deselect it, otherwise select new one
    const newId = selectedGenderTagId === tagId ? null : tagId
    console.log('Gender tag toggled:', { tagId, newId })
    setSelectedGenderTagId(newId)
    setSuccessMessage(null)
  }

  const handleToggleInterestTag = (tagId: string) => {
    const newIds = selectedInterestTagIds.includes(tagId)
      ? selectedInterestTagIds.filter(id => id !== tagId)
      : [...selectedInterestTagIds, tagId]

    console.log('Interest tag toggled:', { tagId, newIds })
    setSelectedInterestTagIds(newIds)
    setSuccessMessage(null)
  }

  const handleToggleLookingForTag = (tagId: string) => {
    const newIds = selectedLookingForTagIds.includes(tagId)
      ? selectedLookingForTagIds.filter(id => id !== tagId)
      : [...selectedLookingForTagIds, tagId]

    console.log('Looking for tag toggled:', { tagId, newIds })
    setSelectedLookingForTagIds(newIds)
    setSuccessMessage(null)
  }

  const getTagChipStyle = (categoryColor?: string, isSelected?: boolean) => {
    if (isSelected) {
      return {
        backgroundColor: categoryColor || '#1976d2',
        color: 'white',
        borderColor: categoryColor || '#1976d2',
      }
    }
    return {
      borderColor: categoryColor || '#ccc',
      color: categoryColor || '#666',
    }
  }

  if (isLoading || isLoadingTags) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    )
  }

  const socialLinksArray = Object.entries(formData.socialMediaLinks || {})
  const displayNameStatus = canChangeDisplayName(profile)

  // Check if all platforms are already in use
  const usedPlatforms = Object.values(formData.socialMediaLinks || {}).map(link => link.platform)
  const allPlatformsUsed = platformOptions.every(platform => usedPlatforms.includes(platform))

  return (
    <Box>
      {/* Error Dialog */}
      {error && (
        <ErrorDialog
          open={true}
          title={error.title}
          message={error.message}
          details={error.details}
          onClose={() => setError(null)}
        />
      )}

      {/* Success Message */}
      {successMessage && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccessMessage(null)}>
          {successMessage}
        </Alert>
      )}

      {/* Display Name Restriction Warning */}
      {!displayNameStatus.canChange && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          <Typography variant="body2" sx={{ mb: 0.5 }}>
            <strong>Display Name Change Restricted</strong>
          </Typography>
          <Typography variant="body2">
            {displayNameStatus.reason}
          </Typography>
        </Alert>
      )}

      {/* Cover Photo */}
      <Paper elevation={0} sx={{ p: 3, mb: 3, border: '1px solid', borderColor: 'divider' }}>
        <Typography variant="h6" gutterBottom>
          Cover Photo
        </Typography>
        <ImageUploadZone
          value={formData.coverPhoto}
          onFileSelect={handleCoverPhotoUpload}
          onClear={handleClearCover}
          maxSizeMB={10}
          label="Upload Cover Photo"
          description="Drag and drop, paste, or click to select"
          isUploading={isUploadingCover}
          aspectRatio="3:1"
          allowCamera={true}
          onSelectFromFiles={() => setCoverSelectorOpen(true)}
        />
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
          Recommended size: 1200 x 400 pixels • Max 10MB
        </Typography>
      </Paper>

      {/* Profile Picture */}
      <Paper elevation={0} sx={{ p: 3, mb: 3, border: '1px solid', borderColor: 'divider' }}>
        <Typography variant="h6" gutterBottom>
          Profile Picture
        </Typography>
        <ImageUploadZone
          value={formData.avatar}
          onFileSelect={handleAvatarUpload}
          onClear={handleClearAvatar}
          maxSizeMB={2}
          label="Upload Profile Picture"
          description="Drag and drop, paste, or click to select"
          isUploading={isUploadingAvatar}
          aspectRatio="1:1"
          allowCamera={true}
          onSelectFromFiles={() => setAvatarSelectorOpen(true)}
        />
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
          Recommended size: 200 x 200 pixels • Max 2MB
        </Typography>
      </Paper>

      {/* Basic Information */}
      <Paper elevation={0} sx={{ p: 3, mb: 3, border: '1px solid', borderColor: 'divider' }}>
        <Typography variant="h6" gutterBottom>
          Basic Information
        </Typography>
        <Grid container spacing={3}>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Display Name"
              variant="outlined"
              value={formData.displayName}
              onChange={(e) => handleInputChange('displayName', e.target.value)}
              required
              disabled={!displayNameStatus.canChange}
              helperText={
                !displayNameStatus.canChange
                  ? displayNameStatus.reason
                  : displayNameStatus.changesRemaining !== undefined
                  ? `You have ${displayNameStatus.changesRemaining} change${displayNameStatus.changesRemaining === 1 ? '' : 's'} remaining this year`
                  : undefined
              }
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth variant="outlined">
              <InputLabel>Profile Visibility</InputLabel>
              <Select
                value={formData.profileVisibility}
                onChange={(e) => handleInputChange('profileVisibility', e.target.value as ProfileVisibility)}
                label="Profile Visibility"
              >
                <MenuItem value={0}>Public</MenuItem>
                <MenuItem value={1}>Friends Only</MenuItem>
                <MenuItem value={2}>Private</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Tell others about yourself"
              variant="outlined"
              value={formData.bio}
              onChange={(e) => handleInputChange('bio', e.target.value)}
              multiline
              rows={4}
              placeholder="Write a short bio about yourself..."
            />
          </Grid>
        </Grid>
      </Paper>

      {/* Birth Date */}
      <Paper elevation={0} sx={{ p: 3, mb: 3, border: '1px solid', borderColor: 'divider' }}>
        <Typography variant="h6" gutterBottom>
          Birth Date
        </Typography>
        {/* Show warning if birthdate is already set (cannot be changed) */}
        {profile?.ageInMonths != null && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Your birth date has been set and cannot be changed.
          </Alert>
        )}
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth variant="outlined">
              <InputLabel>Birth Month</InputLabel>
              <Select
                value={birthMonth}
                onChange={(e) => handleBirthDateChange(e.target.value as number, birthYear)}
                label="Birth Month"
                disabled={profile?.ageInMonths != null}
              >
                {Array.from({ length: 12 }, (_, i) => i + 1).map((month) => (
                  <MenuItem key={month} value={month}>
                    {new Date(2000, month - 1).toLocaleString('default', { month: 'long' })}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth variant="outlined">
              <InputLabel>Birth Year</InputLabel>
              <Select
                value={birthYear}
                onChange={(e) => handleBirthDateChange(birthMonth, e.target.value as number)}
                label="Birth Year"
                disabled={profile?.ageInMonths != null}
              >
                {Array.from({ length: 100 }, (_, i) => new Date().getFullYear() - i).map((year) => (
                  <MenuItem key={year} value={year}>
                    {year}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <FormControl fullWidth>
              <InputLabel>Age Visibility</InputLabel>
              <Select
                value={profile?.ageInMonths != null ? formData.showAge : 0}
                label="Age Visibility"
                onChange={(e) => handleInputChange('showAge', e.target.value as AgeVisibility)}
                disabled={profile?.ageInMonths == null}
              >
                <MenuItem value={0}>Private - Don't show my age</MenuItem>
                <MenuItem value={1}>Age Range - Show general range (e.g., "20s", "30s", "40s")</MenuItem>
                <MenuItem value={2}>Age - Show age in years (e.g., "25")</MenuItem>
                <MenuItem value={3}>Age with Month - Show age with month precision (e.g., "25 years, 3 months")</MenuItem>
              </Select>
              {profile?.ageInMonths == null && (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                  Set your birth date first to configure age visibility
                </Typography>
              )}
            </FormControl>
          </Grid>
        </Grid>
      </Paper>

      {/* Gender Identity */}
      <Paper elevation={0} sx={{ p: 3, mb: 3, border: '1px solid', borderColor: 'divider' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 2 }}>
          <Box>
            <Typography variant="h6" gutterBottom sx={{ mb: 0.5 }}>
              Gender Identity
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Select one option that best describes your gender identity (optional)
            </Typography>
          </Box>
          {/* Visibility Control */}
          <ToggleButtonGroup
            value={formData.genderVisibility}
            exclusive
            onChange={(e, newValue) => {
              if (newValue !== null) {
                handleInputChange('genderVisibility', newValue)
              }
            }}
            size="small"
            sx={{ flexShrink: 0 }}
          >
            <ToggleButton value={0}>
              <Tooltip title="Private (Only Me)" enterTouchDelay={0} arrow>
                <Lock fontSize="small" />
              </Tooltip>
            </ToggleButton>
            <ToggleButton value={1}>
              <Tooltip title="Friends Only" enterTouchDelay={0} arrow>
                <People fontSize="small" />
              </Tooltip>
            </ToggleButton>
            <ToggleButton value={2}>
              <Tooltip title="Friends & Groups" enterTouchDelay={0} arrow>
                <Groups fontSize="small" />
              </Tooltip>
            </ToggleButton>
            <ToggleButton value={3}>
              <Tooltip title="Public (Everyone)" enterTouchDelay={0} arrow>
                <Public fontSize="small" />
              </Tooltip>
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>

        {/* Selected Gender Tag */}
        {selectedGenderTagId && (
          <Paper variant="outlined" sx={{ p: 2, mb: 2, backgroundColor: '#f5f5f5' }}>
            <Typography variant="subtitle2" gutterBottom>
              Selected
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {(() => {
                const selectedTag = genderTags.find(t => t.id === selectedGenderTagId)
                if (!selectedTag) return null
                const category = genderCategories.find(c => c.id === selectedTag.categoryId)

                return (
                  <Chip
                    label={selectedTag.displayName}
                    onDelete={() => handleToggleGenderTag(selectedTag.id)}
                    deleteIcon={<Close />}
                    sx={{
                      backgroundColor: category?.color || '#2196f3',
                      color: 'white',
                      '& .MuiChip-deleteIcon': {
                        color: 'white',
                        '&:hover': {
                          color: 'rgba(255, 255, 255, 0.7)',
                        },
                      },
                    }}
                  />
                )
              })()}
            </Box>
          </Paper>
        )}

        {(() => {
          const activeCategories = genderCategories.filter((c) => c.isActive && c.tags && c.tags.some((t) => t.isActive))

          // If only one category, show chips directly
          if (activeCategories.length === 1) {
            const category = activeCategories[0]
            return (
              <Box>
                {category.description && (
                  <Typography variant="caption" sx={{ mb: 2, display: 'block', color: '#666' }}>
                    {category.description}
                  </Typography>
                )}
                <Box className="tag-chips-container">
                  {category.tags
                    ?.filter((t) => t.isActive)
                    .sort((a, b) => {
                      const orderDiff = a.sortOrder - b.sortOrder
                      return orderDiff !== 0 ? orderDiff : a.displayName.localeCompare(b.displayName)
                    })
                    .map((tag) => {
                      const isSelected = selectedGenderTagId === tag.id
                      return (
                        <Chip
                          key={tag.id}
                          label={
                            <>
                              {tag.icon && <span className="tag-icon">{tag.icon}</span>}
                              <span className="tag-label">{tag.displayName}</span>
                            </>
                          }
                          onClick={() => handleToggleGenderTag(tag.id)}
                          variant={isSelected ? 'filled' : 'outlined'}
                          size="small"
                          sx={getTagChipStyle(category.color, isSelected)}
                          title={tag.description}
                          className="tag-chip"
                        />
                      )
                    })}
                </Box>
              </Box>
            )
          }

          // Multiple categories, use accordions
          return activeCategories.map((category) => (
            <Accordion
              key={category.id}
              expanded={expandedGenderCategory === category.id}
              onChange={(_, isExpanded) => setExpandedGenderCategory(isExpanded ? category.id : null)}
            >
              <AccordionSummary
                expandIcon={<ExpandMore sx={{ fontSize: 20 }} />}
                sx={{
                  minHeight: '40px',
                  '&.Mui-expanded': { minHeight: '40px' },
                  '& .MuiAccordionSummary-content': {
                    my: 0.75,
                    '&.Mui-expanded': { my: 0.75 }
                  }
                }}
              >
                <Box className="category-header">
                  <Box className="category-header-left">
                    <span className="category-icon" style={{ fontSize: '16px' }}>{category.icon || '📁'}</span>
                    <span style={{ color: category.color, fontWeight: 500, fontSize: '0.875rem' }}>
                      {category.displayName}
                      <small className="badge badge-single" style={{ fontSize: '0.65rem' }}>Single choice</small>
                    </span>
                  </Box>
                  <Box className="category-header-right">
                    <span
                      className="tag-count-badge"
                      style={{
                        backgroundColor: category.color,
                        color: 'white',
                        fontSize: '0.7rem',
                        padding: '2px 6px',
                        minWidth: '20px',
                        height: '18px',
                      }}
                    >
                      {category.tags?.filter((t) => t.isActive).length || 0}
                    </span>
                  </Box>
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                {category.description && (
                  <Typography variant="caption" sx={{ mb: 2, display: 'block', color: '#666' }}>
                    {category.description}
                  </Typography>
                )}
                <Box className="tag-chips-container">
                  {category.tags
                    ?.filter((t) => t.isActive)
                    .sort((a, b) => {
                      const orderDiff = a.sortOrder - b.sortOrder
                      return orderDiff !== 0 ? orderDiff : a.displayName.localeCompare(b.displayName)
                    })
                    .map((tag) => {
                      const isSelected = selectedGenderTagId === tag.id
                      return (
                        <Chip
                          key={tag.id}
                          label={
                            <>
                              {tag.icon && <span className="tag-icon">{tag.icon}</span>}
                              <span className="tag-label">{tag.displayName}</span>
                            </>
                          }
                          onClick={() => handleToggleGenderTag(tag.id)}
                          variant={isSelected ? 'filled' : 'outlined'}
                          size="small"
                          sx={getTagChipStyle(category.color, isSelected)}
                          title={tag.description}
                          className="tag-chip"
                        />
                      )
                    })}
                </Box>
              </AccordionDetails>
            </Accordion>
          ))
        })()}
      </Paper>

      {/* Interests */}
      <Paper elevation={0} sx={{ p: 3, mb: 3, border: '1px solid', borderColor: 'divider' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 2 }}>
          <Box>
            <Typography variant="h6" gutterBottom sx={{ mb: 0.5 }}>
              Interests
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Select all interests that apply to you
            </Typography>
          </Box>
          {/* Visibility Control */}
          <ToggleButtonGroup
            value={formData.interestsVisibility}
            exclusive
            onChange={(e, newValue) => {
              if (newValue !== null) {
                handleInputChange('interestsVisibility', newValue)
              }
            }}
            size="small"
            sx={{ flexShrink: 0 }}
          >
            <ToggleButton value={0}>
              <Tooltip title="Private (Only Me)" enterTouchDelay={0} arrow>
                <Lock fontSize="small" />
              </Tooltip>
            </ToggleButton>
            <ToggleButton value={1}>
              <Tooltip title="Friends Only" enterTouchDelay={0} arrow>
                <People fontSize="small" />
              </Tooltip>
            </ToggleButton>
            <ToggleButton value={2}>
              <Tooltip title="Friends & Groups" enterTouchDelay={0} arrow>
                <Groups fontSize="small" />
              </Tooltip>
            </ToggleButton>
            <ToggleButton value={3}>
              <Tooltip title="Public (Everyone)" enterTouchDelay={0} arrow>
                <Public fontSize="small" />
              </Tooltip>
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>

        {/* Selected Interest Tags */}
        {selectedInterestTagIds.length > 0 && (
          <Paper variant="outlined" sx={{ p: 2, mb: 2, backgroundColor: '#f5f5f5' }}>
            <Typography variant="subtitle2" gutterBottom>
              Selected Interests ({selectedInterestTagIds.length})
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {selectedInterestTagIds.map((tagId) => {
                const tag = interestTags.find(t => t.id === tagId)
                if (!tag) return null
                const category = interestCategories.find(c => c.id === tag.categoryId)

                return (
                  <Chip
                    key={tagId}
                    label={tag.displayName}
                    onDelete={() => handleToggleInterestTag(tagId)}
                    deleteIcon={<Close />}
                    sx={{
                      backgroundColor: category?.color || '#2196f3',
                      color: 'white',
                      '& .MuiChip-deleteIcon': {
                        color: 'white',
                        '&:hover': {
                          color: 'rgba(255, 255, 255, 0.7)',
                        },
                      },
                    }}
                  />
                )
              })}
            </Box>
          </Paper>
        )}

        {(() => {
          const activeCategories = interestCategories.filter((c) => c.isActive && c.tags && c.tags.some((t) => t.isActive))

          // If only one category, show chips directly
          if (activeCategories.length === 1) {
            const category = activeCategories[0]
            return (
              <Box>
                {category.description && (
                  <Typography variant="caption" sx={{ mb: 2, display: 'block', color: '#666' }}>
                    {category.description}
                  </Typography>
                )}
                <Box className="tag-chips-container">
                  {category.tags
                    ?.filter((t) => t.isActive)
                    .sort((a, b) => {
                      const orderDiff = a.sortOrder - b.sortOrder
                      return orderDiff !== 0 ? orderDiff : a.displayName.localeCompare(b.displayName)
                    })
                    .map((tag) => {
                      const isSelected = selectedInterestTagIds.includes(tag.id)
                      return (
                        <Chip
                          key={tag.id}
                          label={
                            <>
                              {tag.icon && <span className="tag-icon">{tag.icon}</span>}
                              <span className="tag-label">{tag.displayName}</span>
                            </>
                          }
                          onClick={() => handleToggleInterestTag(tag.id)}
                          variant={isSelected ? 'filled' : 'outlined'}
                          size="small"
                          sx={getTagChipStyle(category.color, isSelected)}
                          title={tag.description}
                          className="tag-chip"
                        />
                      )
                    })}
                </Box>
              </Box>
            )
          }

          // Multiple categories, use accordions
          return activeCategories.map((category) => (
            <Accordion
              key={category.id}
              expanded={expandedInterestCategory === category.id}
              onChange={(_, isExpanded) => setExpandedInterestCategory(isExpanded ? category.id : null)}
            >
              <AccordionSummary
                expandIcon={<ExpandMore sx={{ fontSize: 20 }} />}
                sx={{
                  minHeight: '40px',
                  '&.Mui-expanded': { minHeight: '40px' },
                  '& .MuiAccordionSummary-content': {
                    my: 0.75,
                    '&.Mui-expanded': { my: 0.75 }
                  }
                }}
              >
                <Box className="category-header">
                  <Box className="category-header-left">
                    <span className="category-icon" style={{ fontSize: '16px' }}>{category.icon || '📁'}</span>
                    <span style={{ color: category.color, fontWeight: 500, fontSize: '0.875rem' }}>
                      {category.displayName}
                    </span>
                  </Box>
                  <Box className="category-header-right">
                    <span
                      className="tag-count-badge"
                      style={{
                        backgroundColor: category.color,
                        color: 'white',
                        fontSize: '0.7rem',
                        padding: '2px 6px',
                        minWidth: '20px',
                        height: '18px',
                      }}
                    >
                      {category.tags?.filter((t) => t.isActive).length || 0}
                    </span>
                  </Box>
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                {category.description && (
                  <Typography variant="caption" sx={{ mb: 2, display: 'block', color: '#666' }}>
                    {category.description}
                  </Typography>
                )}
                <Box className="tag-chips-container">
                  {category.tags
                    ?.filter((t) => t.isActive)
                    .sort((a, b) => {
                      const orderDiff = a.sortOrder - b.sortOrder
                      return orderDiff !== 0 ? orderDiff : a.displayName.localeCompare(b.displayName)
                    })
                    .map((tag) => {
                      const isSelected = selectedInterestTagIds.includes(tag.id)
                      return (
                        <Chip
                          key={tag.id}
                          label={
                            <>
                              {tag.icon && <span className="tag-icon">{tag.icon}</span>}
                              <span className="tag-label">{tag.displayName}</span>
                            </>
                          }
                          onClick={() => handleToggleInterestTag(tag.id)}
                          variant={isSelected ? 'filled' : 'outlined'}
                          size="small"
                          sx={getTagChipStyle(category.color, isSelected)}
                          title={tag.description}
                          className="tag-chip"
                        />
                      )
                    })}
                </Box>
              </AccordionDetails>
            </Accordion>
          ))
        })()}
      </Paper>

      {/* Looking For */}
      <Paper elevation={0} sx={{ p: 3, mb: 3, border: '1px solid', borderColor: 'divider' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 2 }}>
          <Box>
            <Typography variant="h6" gutterBottom sx={{ mb: 0.5 }}>
              Looking For
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Select what you're looking for in adventures
            </Typography>
          </Box>
          {/* Visibility Control */}
          <ToggleButtonGroup
            value={formData.lookingForVisibility}
            exclusive
            onChange={(e, newValue) => {
              if (newValue !== null) {
                handleInputChange('lookingForVisibility', newValue)
              }
            }}
            size="small"
            sx={{ flexShrink: 0 }}
          >
            <ToggleButton value={0}>
              <Tooltip title="Private (Only Me)" enterTouchDelay={0} arrow>
                <Lock fontSize="small" />
              </Tooltip>
            </ToggleButton>
            <ToggleButton value={1}>
              <Tooltip title="Friends Only" enterTouchDelay={0} arrow>
                <People fontSize="small" />
              </Tooltip>
            </ToggleButton>
            <ToggleButton value={2}>
              <Tooltip title="Friends & Groups" enterTouchDelay={0} arrow>
                <Groups fontSize="small" />
              </Tooltip>
            </ToggleButton>
            <ToggleButton value={3}>
              <Tooltip title="Public (Everyone)" enterTouchDelay={0} arrow>
                <Public fontSize="small" />
              </Tooltip>
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>

        {/* Selected Looking For Tags */}
        {selectedLookingForTagIds.length > 0 && (
          <Paper variant="outlined" sx={{ p: 2, mb: 2, backgroundColor: '#f5f5f5' }}>
            <Typography variant="subtitle2" gutterBottom>
              Selected ({selectedLookingForTagIds.length})
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {selectedLookingForTagIds.map((tagId) => {
                const tag = lookingForTags.find(t => t.id === tagId)
                if (!tag) return null
                const category = lookingForCategories.find(c => c.id === tag.categoryId)

                return (
                  <Chip
                    key={tagId}
                    label={tag.displayName}
                    onDelete={() => handleToggleLookingForTag(tagId)}
                    deleteIcon={<Close />}
                    sx={{
                      backgroundColor: category?.color || '#2196f3',
                      color: 'white',
                      '& .MuiChip-deleteIcon': {
                        color: 'white',
                        '&:hover': {
                          color: 'rgba(255, 255, 255, 0.7)',
                        },
                      },
                    }}
                  />
                )
              })}
            </Box>
          </Paper>
        )}

        {(() => {
          const activeCategories = lookingForCategories.filter((c) =>
            c.isActive && c.tags && c.tags.some((t) => t.isActive))

          // If only one category, show chips directly without accordion container
          if (activeCategories.length === 1) {
            const category = activeCategories[0]
            return (
              <Box>
                {category.description && (
                  <Typography variant="caption" sx={{ mb: 2, display: 'block', color: '#666' }}>
                    {category.description}
                  </Typography>
                )}
                <Box className="tag-chips-container">
                  {category.tags
                    ?.filter((t) => t.isActive)
                    .sort((a, b) => {
                      const orderDiff = a.sortOrder - b.sortOrder
                      return orderDiff !== 0 ? orderDiff : a.displayName.localeCompare(b.displayName)
                    })
                    .map((tag) => {
                      const isSelected = selectedLookingForTagIds.includes(tag.id)
                      return (
                        <Chip
                          key={tag.id}
                          label={
                            <>
                              {tag.icon && <span className="tag-icon">{tag.icon}</span>}
                              <span className="tag-label">{tag.displayName}</span>
                            </>
                          }
                          onClick={() => handleToggleLookingForTag(tag.id)}
                          variant={isSelected ? 'filled' : 'outlined'}
                          size="small"
                          sx={getTagChipStyle(category.color, isSelected)}
                          title={tag.description}
                          className="tag-chip"
                        />
                      )
                    })}
                </Box>
              </Box>
            )
          }

          // Multiple categories, use accordions with controlled expansion
          return activeCategories.map((category) => (
            <Accordion
              key={category.id}
              expanded={expandedLookingForCategory === category.id}
              onChange={(_, isExpanded) => setExpandedLookingForCategory(isExpanded ? category.id : null)}
            >
              <AccordionSummary
                expandIcon={<ExpandMore sx={{ fontSize: 20 }} />}
                sx={{
                  minHeight: '40px',
                  '&.Mui-expanded': { minHeight: '40px' },
                  '& .MuiAccordionSummary-content': {
                    my: 0.75,
                    '&.Mui-expanded': { my: 0.75 }
                  }
                }}
              >
                <Box className="category-header">
                  <Box className="category-header-left">
                    <span className="category-icon" style={{ fontSize: '16px' }}>{category.icon || '📁'}</span>
                    <span style={{ color: category.color, fontWeight: 500, fontSize: '0.875rem' }}>
                      {category.displayName}
                    </span>
                  </Box>
                  <Box className="category-header-right">
                    <span
                      className="tag-count-badge"
                      style={{
                        backgroundColor: category.color,
                        color: 'white',
                        fontSize: '0.7rem',
                        padding: '2px 6px',
                        minWidth: '20px',
                        height: '18px',
                      }}
                    >
                      {category.tags?.filter((t) => t.isActive).length || 0}
                    </span>
                  </Box>
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                {category.description && (
                  <Typography variant="caption" sx={{ mb: 2, display: 'block', color: '#666' }}>
                    {category.description}
                  </Typography>
                )}
                <Box className="tag-chips-container">
                  {category.tags
                    ?.filter((t) => t.isActive)
                    .sort((a, b) => {
                      const orderDiff = a.sortOrder - b.sortOrder
                      return orderDiff !== 0 ? orderDiff : a.displayName.localeCompare(b.displayName)
                    })
                    .map((tag) => {
                      const isSelected = selectedLookingForTagIds.includes(tag.id)
                      return (
                        <Chip
                          key={tag.id}
                          label={
                            <>
                              {tag.icon && <span className="tag-icon">{tag.icon}</span>}
                              <span className="tag-label">{tag.displayName}</span>
                            </>
                          }
                          onClick={() => handleToggleLookingForTag(tag.id)}
                          variant={isSelected ? 'filled' : 'outlined'}
                          size="small"
                          sx={getTagChipStyle(category.color, isSelected)}
                          title={tag.description}
                          className="tag-chip"
                        />
                      )
                    })}
                </Box>
              </AccordionDetails>
            </Accordion>
          ))
        })()}
      </Paper>

      {/* Social Media Links */}
      <Paper elevation={0} sx={{ p: 3, mb: 3, border: '1px solid', borderColor: 'divider' }}>
        <Box sx={{ mb: 2 }}>
          <Typography variant="h6" gutterBottom sx={{ mb: 0.5 }}>
            Social Media Links
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Add your social media profiles and set individual privacy for each link
          </Typography>
        </Box>

        {socialLinksArray.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            No social media links added yet
          </Typography>
        ) : (
          <Box sx={{ mb: 2 }}>
            {socialLinksArray.map(([key, link]) => (
              <Box key={key} sx={{ mb: 2, p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} sm={3}>
                    <FormControl fullWidth size="small" variant="outlined">
                      <InputLabel>Platform</InputLabel>
                      <Select
                        value={link.platform}
                        onChange={(e) => handleUpdateSocialLink(key, 'platform', e.target.value)}
                        label="Platform"
                      >
                        {platformOptions.map((platform) => {
                          // Disable platforms that are already in use (except the current one)
                          const isUsedByOther = Object.entries(formData.socialMediaLinks || {})
                            .filter(([k, _]) => k !== key)
                            .some(([_, l]) => l.platform === platform)

                          return (
                            <MenuItem key={platform} value={platform} disabled={isUsedByOther}>
                              <Box display="flex" alignItems="center" gap={1}>
                                {platformIcons[platform]}
                                {platform}
                                {isUsedByOther && <Typography variant="caption" color="text.secondary"> (in use)</Typography>}
                              </Box>
                            </MenuItem>
                          )
                        })}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={5}>
                    <TextField
                      fullWidth
                      size="small"
                      label={link.platform === 'Other' ? 'Profile URL' : 'Username or Profile URL'}
                      variant="outlined"
                      value={link.url}
                      onChange={(e) => handleUpdateSocialLink(key, 'url', e.target.value)}
                      error={!!socialLinkErrors[key]}
                      placeholder={
                        link.platform === 'Other'
                          ? 'https://...'
                          : link.platform === 'Instagram'
                          ? 'johnsmith or https://instagram.com/johnsmith'
                          : link.platform === 'Twitter'
                          ? '@johnsmith or https://twitter.com/johnsmith'
                          : link.platform === 'YouTube'
                          ? '@channel or https://youtube.com/@channel'
                          : 'username or profile URL'
                      }
                      helperText={
                        socialLinkErrors[key] ||
                        (link.platform !== 'Other' && link.url && !socialLinkErrors[key]
                          ? `Profile link: ${generatePlatformUrl(link.url, link.platform)}`
                          : link.platform === 'Other' && link.url && !socialLinkErrors[key]
                          ? `Full URL: ${generatePlatformUrl(link.url, link.platform)}`
                          : '')
                      }
                    />
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <ToggleButtonGroup
                      value={link.visibility ?? 3}
                      exclusive
                      onChange={(e, newValue) => {
                        if (newValue !== null) {
                          handleUpdateSocialLink(key, 'visibility', newValue)
                        }
                      }}
                      size="small"
                      fullWidth
                    >
                      <ToggleButton value={0}>
                        <Tooltip title="Private (Only Me)" enterTouchDelay={0} arrow>
                          <Lock fontSize="small" />
                        </Tooltip>
                      </ToggleButton>
                      <ToggleButton value={1}>
                        <Tooltip title="Friends Only" enterTouchDelay={0} arrow>
                          <People fontSize="small" />
                        </Tooltip>
                      </ToggleButton>
                      <ToggleButton value={2}>
                        <Tooltip title="Friends & Groups" enterTouchDelay={0} arrow>
                          <Groups fontSize="small" />
                        </Tooltip>
                      </ToggleButton>
                      <ToggleButton value={3}>
                        <Tooltip title="Public (Everyone)" enterTouchDelay={0} arrow>
                          <Public fontSize="small" />
                        </Tooltip>
                      </ToggleButton>
                    </ToggleButtonGroup>
                  </Grid>
                  <Grid item xs={12} sm={1}>
                    <IconButton color="error" onClick={() => handleRemoveSocialLink(key)} size="small">
                      <Delete />
                    </IconButton>
                  </Grid>
                </Grid>
              </Box>
            ))}
          </Box>
        )}

        <Button
          variant="outlined"
          startIcon={<Add />}
          onClick={handleAddSocialLink}
          disabled={allPlatformsUsed}
        >
          Add Social Media Link
        </Button>
        {allPlatformsUsed && (
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            All available platforms have been added
          </Typography>
        )}
      </Paper>

      {/* Action Buttons */}
      <Box display="flex" justifyContent="flex-end" gap={2}>
        <Button variant="outlined" onClick={loadProfile} disabled={isSaving}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={isSaving}
          startIcon={isSaving ? <CircularProgress size={16} /> : <Save />}
        >
          {isSaving ? 'Saving...' : 'Save Changes'}
        </Button>
      </Box>

      {/* Image Selector Dialogs */}
      <ProfileImageSelectorDialog
        open={avatarSelectorOpen}
        onClose={() => setAvatarSelectorOpen(false)}
        onSelect={handleAvatarFromFiles}
        title="Select Profile Picture"
        description="Choose an existing image from your uploaded files"
      />
      <ProfileImageSelectorDialog
        open={coverSelectorOpen}
        onClose={() => setCoverSelectorOpen(false)}
        onSelect={handleCoverFromFiles}
        title="Select Cover Photo"
        description="Choose an existing image from your uploaded files"
      />
    </Box>
  )
}
